import { FunctionComponent, memo, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useNavigate, useParams } from 'react-router-dom';
import { PostedIn } from '../../../components';
import { NoContent } from '../../../pages';
import { StoryCard } from '../../../services';
import { COLLAPSE_STORAGE_KEY } from '../../constants';
import { useAppSelector, useHasHistory, useTagsFeed } from '../../hooks';
import { isAdminLayout } from '../../utils';
import { CollapseSwitcher } from '../CollapseSwitcher';
import { FeedCardLoader } from '../FeedCard';
import { IconLabel } from '../IconLabel';
import { TagFollowControls } from './TagFollowControls';

import classes from './TagsFeedCommon.module.scss';

interface TagsFeedCommonProps {
  cardComponent: (card: StoryCard) => JSX.Element;
}

export const TagsFeedCommon: FunctionComponent<TagsFeedCommonProps> = memo(({ cardComponent }) => {
  const { t } = useTranslation();

  const navigate = useNavigate();

  const { getTagsFeedItems, loadMoreTagsFeedItems } = useTagsFeed();

  const { tagsFeed: cards, hasNextPage, isFetched } = useAppSelector(({ tagsFeed }) => tagsFeed);

  const organisationTagId = Number(useParams().organisationTagId);

  const { externalTags = [], externalStatusTags = [] } = { ...cards[0] };

  const tagName = useMemo(
    () => [...externalTags, ...externalStatusTags].find(({ id }) => id === organisationTagId)?.name,
    [externalStatusTags, externalTags, organisationTagId]
  );

  const hasCards = useMemo(() => Boolean(cards.length), [cards.length]);

  const hasHistory = useHasHistory();

  const topbar = useMemo(() => {
    if (!hasHistory && !isAdminLayout()) {
      return null;
    }

    return (
      <div className={classes['tags-feed__topbar']}>
        {hasHistory && (
          <IconLabel
            iconId={'arrow-rounded-left'}
            iconSize={18}
            label={t('common.back')}
            labelClassName={classes['tags-feed__topbar-label']}
            onClick={() => navigate(-1)}
            color={'#43445b'}
            singleColor
          />
        )}

        {isAdminLayout() && (
          <CollapseSwitcher
            storageKey={COLLAPSE_STORAGE_KEY}
            className={classes['tags-feed__topbar-switcher']}
          />
        )}
      </div>
    );
  }, [hasHistory, navigate, t]);

  useEffect(() => {
    getTagsFeedItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationTagId]);

  const content = useMemo(() => {
    if (!hasCards) {
      return <NoContent />;
    }

    return (
      <InfiniteScroll
        next={loadMoreTagsFeedItems}
        hasMore={hasNextPage}
        loader={<FeedCardLoader />}
        dataLength={cards.length}
        className={classes['tags-feed__content']}
      >
        <div className={classes['tags-feed__content-cards']}>
          {cards.map((card, index, cardsArray) => (
            <div key={card.id}>
              {card.story.id !== cardsArray[index - 1]?.story.id && <PostedIn story={card.story} />}
              {cardComponent(card)}
            </div>
          ))}
        </div>
      </InfiniteScroll>
    );
  }, [cardComponent, cards, hasCards, hasNextPage, loadMoreTagsFeedItems]);

  if (!isFetched) {
    return <FeedCardLoader />;
  }

  return (
    <div className={classes['tags-feed']}>
      {topbar}

      {tagName && (
        <div className={classes['tags-feed__header']}>
          <div className={classes['tags-feed__header-title']}>{`#${tagName}`}</div>
          <div className={classes['tags-feed__header-controls']}>
            <TagFollowControls tagId={organisationTagId} />
          </div>
        </div>
      )}

      {content}
    </div>
  );
});
